import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { OuterDiv, MainSection, WorkloadSection } from './styles';
import { Header, HeaderLeft, HeaderTitle } from '../../pages/report/styles';
import Chip from '../chipComponent';
import { CHIP_ICON_POSITION, CHIP_SIZE, CHIP_TYPE } from '../../global/constants';
import WorkloadFilterBar from './workloadFilterBar';
import WorkloadLeftSection from './workloadLeftSection';
import WorkloadRightSection from './workloadRightSection';
import { SelectRangeInterface } from '../../interfaces/ReportInterface';
import { rootStore } from '../../mobx/rootStore';
import { getWorkloadReportData } from '../../services/reportService';
import { isEmpty } from '../../helpers/common';
import { observer } from 'mobx-react-lite';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.extend(isoWeek);

const ReportWorkLoad = () => {
  // States for date selection
  const [selectionRange, setSelectionRange] = useState<SelectRangeInterface>({
    startDate: dayjs().startOf('isoWeek').toDate(), // Monday of current week
    endDate: dayjs().startOf('isoWeek').add(6, 'day').toDate(), // Sunday of current week
    key: 'selection'
  });

  // State to track selected users from filter bar
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // MobX store
  const { reportStore, workspaceStore } = rootStore;
  const { workloadReportData } = reportStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const workspaceUsers = useMemo(() => (workspace?.users ? workspace.users.slice() : []), [workspace?.users]);

  // Check if next week would be in the future
  const isNextWeekDisabled = useMemo(() => {
    const nextWeekStart = dayjs(selectionRange.startDate).add(7, 'day');
    const currentWeekStart = dayjs().startOf('isoWeek');
    return nextWeekStart.isAfter(currentWeekStart, 'day');
  }, [selectionRange.startDate]);

  const handlePreviousWeek = useCallback(() => {
    const newStartDate = dayjs(selectionRange.startDate).subtract(7, 'day');
    const newEndDate = dayjs(newStartDate).add(6, 'day');
    setSelectionRange({
      startDate: newStartDate.toDate(),
      endDate: newEndDate.toDate(),
      key: 'selection'
    });
  }, [selectionRange]);

  const handleNextWeek = useCallback(() => {
    const nextWeekStart = dayjs(selectionRange.startDate).add(7, 'day');
    const currentWeekStart = dayjs().startOf('isoWeek');
    // Don't allow navigation to future weeks
    if (nextWeekStart.isAfter(currentWeekStart, 'day')) {
      return;
    }
    const newStartDate = dayjs(selectionRange.startDate).add(7, 'day');
    const newEndDate = dayjs(newStartDate).add(6, 'day');
    setSelectionRange({
      startDate: newStartDate.toDate(),
      endDate: newEndDate.toDate(),
      key: 'selection'
    });
  }, [selectionRange]);

  const loadWorkloadData = useCallback(async () => {
    try {
      if (!isEmpty(workspace?.id) && selectedUserIds.length > 0) {
        setLoading(true);
        const startDate = dayjs(selectionRange.startDate).format('YYYY-MM-DD');
        const endDate = dayjs(selectionRange.endDate).format('YYYY-MM-DD');

        await getWorkloadReportData(
          selectedUserIds,
          startDate,
          endDate,
          8 // regularHoursPerDay
        );
      } else {
        // Skip API call if required parameters are missing
        return;
      }
    } catch (error) {
      console.error('Error loading workload data:', error);
    } finally {
      if (!isEmpty(workspace?.id) && selectedUserIds.length > 0) setLoading(false);
    }
  }, [workspace?.id, selectedUserIds, selectionRange.startDate, selectionRange.endDate]);

  useEffect(() => {
    loadWorkloadData();
  }, [loadWorkloadData]);

  return (
    <>
      <OuterDiv className='desktop-content'>
        {/* main header */}
        <Header isBottomBorder={true}>
          <HeaderLeft>
            <HeaderTitle>Workload</HeaderTitle>
          </HeaderLeft>
          <Chip
            title={`${dayjs(selectionRange.startDate).format('DD MMM')} - ${dayjs(selectionRange.endDate).format(
              'DD MMM, YYYY'
            )}`}
            variant={CHIP_TYPE.FILL}
            className='date-selector'
            size={CHIP_SIZE.SMALL}
            leadingIconButtonTitle='new-large-back-arrow-icon'
            onLeadingIconButtonClick={handlePreviousWeek}
            trailingIconButtonTitle='updated-large-right-arrow-icon'
            onTrailingIconButtonClick={isNextWeekDisabled ? undefined : handleNextWeek}
            iconPosition={CHIP_ICON_POSITION.AVATAR_TEXT_RIGHT_BUTTON}
            iconButtonClassName='icon-button'
          />
        </Header>
        <MainSection className='desktop-workload-content'>
          {/* workload filter bar */}
          <WorkloadFilterBar selectionRange={selectionRange} onUsersChange={setSelectedUserIds} />
          {/* bottom section */}
          <WorkloadSection>
            {/* left section */}
            <WorkloadLeftSection
              workloadData={workloadReportData}
              workspaceUsers={workspaceUsers}
              selectedUserIds={selectedUserIds}
            />
            {/* right section */}
            <WorkloadRightSection
              workloadData={workloadReportData}
              loading={loading}
              workspaceUsers={workspaceUsers}
              selectedUserIds={selectedUserIds}
              selectionRange={selectionRange}
            />
          </WorkloadSection>
        </MainSection>
      </OuterDiv>
    </>
  );
};

export default observer(ReportWorkLoad);
